import React from 'react';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
import { discoverImages } from '../../../data/discoverVisual';
import { useDiscover } from '../../../contexts/DiscoverContext';
import { cn } from '../../../utils/cn';

/**
 * The editorial discovery hero.
 *
 * One question leads — what are you looking for — and the search is the largest
 * interactive element on the screen, because on a discovery surface everything
 * else is a way of narrowing what the search returns.
 */
const SCOPES = [{
  label: 'University',
  section: 'Universities' as const
}, {
  label: 'Programme',
  section: 'Programmes' as const
}, {
  label: 'Scholarship',
  section: 'Scholarships' as const
}];
export function HeroA() {
  const {
    query,
    setQuery,
    section,
    setSection,
    filters,
    setFilter,
    locations,
    fields
  } = useDiscover();
  return <section className="relative overflow-hidden rounded-panel border border-line bg-surface" data-id="element-1257" data-mp="2ewgbekawt9z1">
      {/* The photograph sits behind the copy and fades into the surface, so the
          composition has no visible image edge cutting the panel in half. */}
      <img src={discoverImages.hero} alt="A university student checking programme deadlines on her phone on campus" className="absolute inset-y-0 right-0 hidden h-full w-[58%] object-cover object-[30%_center] md:block" data-id="element-1258" data-mp="2ae9fw9mhkp0j" />
      <div aria-hidden className="absolute inset-0 hidden bg-gradient-to-r from-surface via-surface/95 to-surface/10 md:block" data-id="element-1259" data-mp="20ny2lwgwx3gz" />

      <div className="relative px-5 py-8 sm:px-8 sm:py-10 lg:w-[62%] lg:py-12" data-id="element-1260" data-mp="1qpinysz2nmod">
        <p className="text-caption font-semibold uppercase tracking-[0.14em] text-brand-700" data-id="element-1261" data-mp="1mof8i7kxrsno">
          Discover your next step
        </p>
        <h1 className="mt-3 text-h1 font-bold leading-[1.1] tracking-tight text-ink lg:text-display" data-id="element-1262" data-mp="1hqdo9jbrxnam">
          Find the university that fits your future.
        </h1>
        <p className="mt-4 max-w-[52ch] text-body-lg leading-7 text-ink-muted" data-id="element-1263" data-mp="3c3mr4s9teajw">
          Explore universities, programmes and funding, matched to your goals, your field and where
          you are in your academic journey.
        </p>

        <form role="search" onSubmit={event => event.preventDefault()} className="mt-7 flex max-w-xl items-center gap-2 rounded-xl border border-line bg-surface p-1.5 shadow-[0_6px_24px_rgba(15,23,42,0.06)] focus-within:border-brand-600" data-id="element-1264" data-mp="3u40nnkfl0i5">
          <SearchIcon aria-hidden className="ml-2.5 h-5 w-5 shrink-0 text-ink-subtle" data-id="element-1265" data-mp="iqbjokk3rvvu" />
          <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search universities, programmes, scholarships…" aria-label="Search universities, programmes and scholarships" className="min-h-touch w-full min-w-0 bg-transparent text-body text-ink outline-none placeholder:text-ink-subtle" data-id="element-1266" data-mp="msolwyzn5bnv" />
          <button type="submit" aria-label="Search" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-700 text-white transition-colors duration-150 ease-out hover:bg-brand-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700" data-id="element-1267" data-mp="2yiqxeko21hkv">
            <ArrowRightIcon aria-hidden className="h-5 w-5" data-id="element-1268" data-mp="2v01kdssasigw" />
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-1269" data-mp="27a7b1l76tc1e">
          {SCOPES.map(scope => <button key={scope.label} type="button" onClick={() => setSection(scope.section)} aria-pressed={section === scope.section} className={cn('min-h-touch rounded-lg border px-4 text-body-sm font-medium transition-colors duration-150 ease-out', section === scope.section ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink')} data-id="element-1270" data-mp="238vs1yitjp26">
              {scope.label}
            </button>)}

          <label className="min-h-touch" data-id="element-1271" data-mp="22783egb8jzuj">
            <span className="sr-only" data-id="element-1272" data-mp="2243pusbvrrr7">Location</span>
            <select value={filters.location} onChange={event => setFilter('location', event.target.value)} className={cn('min-h-touch rounded-lg border px-3.5 text-body-sm font-medium outline-none transition-colors duration-150 ease-out', filters.location === 'All' ? 'border-line bg-surface text-ink-muted' : 'border-brand-700 bg-brand-50 text-brand-800')} data-id="element-1273" data-mp="274rghcw766kw">
              <option value="All" data-id="element-1274" data-mp="2utsvxk3g6hzo">Location</option>
              {locations.filter(item => item !== 'All').map(item => <option key={item} value={item} data-id="element-1275" data-mp="10gtbndo4gzm2">
                    {item}
                  </option>)}
            </select>
          </label>

          <label className="min-h-touch" data-id="element-1276" data-mp="3dsnp4ahwczjy">
            <span className="sr-only" data-id="element-1277" data-mp="39049l7znfuci">Field of study</span>
            <select value={filters.field} onChange={event => setFilter('field', event.target.value)} className={cn('min-h-touch rounded-lg border px-3.5 text-body-sm font-medium outline-none transition-colors duration-150 ease-out', filters.field === 'All' ? 'border-line bg-surface text-ink-muted' : 'border-brand-700 bg-brand-50 text-brand-800')} data-id="element-1278" data-mp="38yjd3ctry1c0">
              <option value="All" data-id="element-1279" data-mp="3nnpd330frvky">Field of study</option>
              {fields.filter(item => item !== 'All').map(item => <option key={item} value={item} data-id="element-1280" data-mp="2cbeprc4gornw">
                    {item}
                  </option>)}
            </select>
          </label>
        </div>

        <button type="button" onClick={() => setQuery('Computer Science')} className="mt-4 text-body-sm text-ink-subtle underline-offset-4 transition-colors duration-150 ease-out hover:text-ink hover:underline" data-id="element-1281" data-mp="28spfut4048o5">
          Try: Computer Science
        </button>
      </div>
    </section>;
}